import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import {
  dequeueModalAtom,
  enqueueModalAtom,
  userTriggeredModalsAtom,
} from '@/atoms/modalQueueAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { ModalPriority } from '@/types/modal-queue.types';
import { Dialog, DialogContent } from '@heidi/design-system/dialog';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';
import {
  HCP_VERIFICATION_MODAL_ID,
  HCP_VERIFICATION_PIN_QUEUE_ITEM,
} from './hcp-verification-atoms';

const HcpVerificationModalContent = dynamic(
  () =>
    import('./hcp-verification-modal-content').then((m) => m.HcpVerificationModalContent),
  { ssr: false }
);

/**
 * The signal the launch row's R1 guard reads. `useIsClinicalRecordingActive`
 * needs the store's timer flag as an argument (Rematch containment), and
 * `GlobalModalsWrapper` has already resolved it into this app state. Selected
 * so the always-mounted shell ignores unrelated app-state changes.
 */
const isClinicalRecordingActiveAtom = selectAtom(
  appStateForModalsAtom,
  (appState) => appState.isClinicalRecordingActive
);

/** A HIGH entry is the launch-row pin; CRITICAL is the clinician's own open. */
const isPinnedByLaunchRowAtom = selectAtom(userTriggeredModalsAtom, (modals) =>
  modals.some(
    (modal) =>
      modal.id === HCP_VERIFICATION_MODAL_ID && modal.priority === ModalPriority.HIGH
  )
);

/**
 * Clinician (NPI) verification dialog. Always mounted by the global modal
 * host; opens when `HCP_VERIFICATION_MODAL_ID` heads the queue, whether the
 * weekly launch row or a settings CTA / inmail click put it there. The body
 * loads lazily and remounts on every open.
 *
 * Every exit goes through `closeWithOptions`, which marks the id seen,
 * dequeues it and POSTs the view; that `last_seen_at` is what the 7-day
 * cooldown reads. Submit is an `engage`; the X, ESC and overlay click all
 * reach `onOpenChange`, which records a `dismiss`.
 *
 * Once on stage the dialog pins itself into the user-triggered list. The
 * launch row is withdrawn the moment the badge probe stops reporting
 * eligible — which a successful submit itself causes: the status refetch the
 * confirm awaits lands before the confirm settles. Unpinned, that would
 * unmount the flow before its toast and `engage` stamp (same pin as
 * `useFirmwareUpdateFlow`).
 *
 * The pin holds against an eligibility flip, not against a consult: a
 * user-triggered entry bypasses the launch row's R1 guard and the tier gate,
 * so the pin stands down when a recording starts. A deliberate CTA open is
 * left alone.
 */
export const HcpVerificationModal = () => {
  const { isOpen, onOpenChange, closeWithOptions } = useQueuedModal(
    HCP_VERIFICATION_MODAL_ID
  );
  const enqueueModal = useSetAtom(enqueueModalAtom);
  const dequeueModal = useSetAtom(dequeueModalAtom);
  const isClinicalRecordingActive = useAtomValue(isClinicalRecordingActiveAtom);
  const isPinnedByLaunchRow = useAtomValue(isPinnedByLaunchRowAtom);

  useEffect(() => {
    if (isOpen && !isClinicalRecordingActive) {
      enqueueModal(HCP_VERIFICATION_PIN_QUEUE_ITEM);
    }
  }, [isOpen, isClinicalRecordingActive, enqueueModal]);

  useEffect(() => {
    if (isClinicalRecordingActive && isPinnedByLaunchRow) {
      dequeueModal(HCP_VERIFICATION_MODAL_ID);
    }
  }, [isClinicalRecordingActive, isPinnedByLaunchRow, dequeueModal]);

  const handleDone = useCallback(
    () => closeWithOptions({ action: ModalViewAction.engage }),
    [closeWithOptions]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[940px] gap-0 overflow-hidden rounded-3xl border-0 bg-surface p-0 sm:rounded-3xl"
        data-testid="hcp-verification-modal"
      >
        {isOpen && <HcpVerificationModalContent onDone={handleDone} />}
      </DialogContent>
    </Dialog>
  );
};
